import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
} from '@mui/material';
import * as React from 'react';
import { vehiclePageStyles } from './styles';
import { useCallback, useEffect, useState, useMemo } from 'react';
import axiosService from 'utils/axios';
import ModalErrorPopup from 'components/Modal/ModalErrorPopup';
import ActionButton from 'components/Shared/Buttons/ActionButton';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Vehicle } from 'types/vehicles/types';
import { STATUS_DELETED, STATUS_NEW_ENTRY, STATUS_NEW_ENTRY_NOT_CONFIRMED, VEHICLE_STATUS_LABEL_MAP } from '../config';
import VehicleStatus from '../status/VehicleStatus';
import './styles.css';
import { ErrorResponse } from 'types/general/types';
import {
  USER_TYPE_ADMIN,
  USER_TYPE_CLIENT,
  USER_TYPE_WAREHOUSE,
  canViewSection,
  canViewField,
  canEditField,
  SECTION_FINANCIALS,
  SECTION_PUBLIC_LINK,
  SECTION_DOCUMENTS,
} from '../../../types/auth/types';
import VehicleGeneralSection, {
  VehicleGeneralSectionData,
  VehicleGeneralSectionRef,
} from './general/VehicleGeneralSection';
import VehicleFinancialsSection, {
  VehicleFinancialsSectionData,
  VehicleFinancialsSectionRef,
} from './financials/VehicleFinancialsSection';
import VehicleShippingSection, {
  VehicleShippingSectionData,
  VehicleShippingSectionRef,
} from './shipping/VehicleShippingSection';
import VehiclePublicLinkSection from './publicLink/VehiclePublicLinkSection';
import VehicleDetailsSection, {
  VehicleDetailsSectionData,
  VehicleDetailsSectionRef,
} from './details/VehicleDetailsSection';
import VehiclePicturesSection, { VehiclePicturesSectionRef } from './pictures/VehiclePicturesSection';
import { UploadProgress } from './hooks/use_file_upload';
import VehicleDocumentsSection, {
  VehicleDocumentsSectionData,
  VehicleDocumentsSectionRef,
} from './documents/VehicleDocumentsSection';
import CircularProgressWithLabel from 'components/Shared/Progress/CircularProgressWithLabel';

function VehiclePage() {
  const { id } = useParams();
  const addingNewVehicle = id === 'new';

  const user = useSelector((state: RootState) => state.auth.user);
  const userType = user?.user_type || '';
  const isSuperuser = user?.user_type === USER_TYPE_ADMIN;

  const newEntryStatus = isSuperuser ? STATUS_NEW_ENTRY : STATUS_NEW_ENTRY_NOT_CONFIRMED;
  const newEntryStatusLabel = VEHICLE_STATUS_LABEL_MAP[newEntryStatus];

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<ErrorResponse | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [vehicle, setVehicle] = useState<Vehicle>();

  // Pictures upload progress state
  const [picturesUploadProgress, setPicturesUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    currentFile: 0,
    totalFiles: 0,
    currentFileName: '',
    percentage: 0,
  });

  const isVehicleNotConfirmed = vehicle?.status?.value === STATUS_NEW_ENTRY_NOT_CONFIRMED;
  const isVehicleDeleted = vehicle?.status?.value === STATUS_DELETED;

  // Permission helper functions (to be passed to child components)
  const canView = useCallback((fieldName: string) => canViewField(userType, fieldName), [userType]);
  const canEdit = useCallback(
    (fieldName: string) => canEditField(userType, fieldName, addingNewVehicle, isVehicleNotConfirmed),
    [userType, addingNewVehicle, isVehicleNotConfirmed]
  );

  // Helper function to determine save button visibility based on user type
  const shouldShowSaveButton = () => {
    if (userType === USER_TYPE_ADMIN || userType === USER_TYPE_WAREHOUSE) {
      // Admin and Warehouse users: Save button always visible
      return true;
    } else if (userType === USER_TYPE_CLIENT) {
      // Client users: Save button visible only when adding new vehicle OR vehicle is not confirmed
      return addingNewVehicle || isVehicleNotConfirmed;
    }
    return false;
  };

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  // Vehicle properties
  const generalSectionRef = React.useRef<VehicleGeneralSectionRef>(null);
  const [generalSectionSubmitData, setGeneralSectionSubmitData] = useState<VehicleGeneralSectionData | null>(null);

  const financialsSectionRef = React.useRef<VehicleFinancialsSectionRef>(null);
  const [financialsSectionSubmitData, setFinancialsSectionSubmitData] = useState<VehicleFinancialsSectionData | null>(
    null
  );

  const shippingSectionRef = React.useRef<VehicleShippingSectionRef>(null);
  const [shippingSectionSubmitData, setShippingSectionSubmitData] = useState<VehicleShippingSectionData | null>(null);

  const detailsSectionRef = React.useRef<VehicleDetailsSectionRef>(null);
  const [detailsSectionSubmitData, setDetailsSectionSubmitData] = useState<VehicleDetailsSectionData | null>(null);

  const picturesSectionRef = React.useRef<VehiclePicturesSectionRef>(null);

  const documentsSectionRef = React.useRef<VehicleDocumentsSectionRef>(null);
  const [documentsSectionSubmitData, setDocumentsSectionSubmitData] = useState<VehicleDocumentsSectionData | null>(
    null
  );

  // Get importerChoice from shippingSectionRef (needed for POA file functionality)
  const importerChoice = shippingSectionRef.current?.getImporterChoice() || null;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Update vehicle state and refresh all section components with new data
  const updateVehicleData = useCallback((data: Vehicle) => {
    setVehicle(data);
    detailsSectionRef.current?.updateSectionData(data);
    generalSectionRef.current?.updateSectionData(data);
    financialsSectionRef.current?.updateSectionData(data);
    shippingSectionRef.current?.updateSectionData(data);
    documentsSectionRef.current?.updateSectionData(data);
  }, []);

  const getVehicle = useCallback(
    (id: string | undefined) => {
      if (id && !addingNewVehicle) {
        setIsLoading(true);
        axiosService
          .get(`/vehicles/vehicle/${id}/`)
          .then(res => updateVehicleData(res.data))
          .catch(() => setErrorMessage({ status: '', statusText: 'Error', message: 'Vehicle not found.' }))
          .finally(() => setIsLoading(false));
      }
    },
    [addingNewVehicle, updateVehicleData]
  );

  useEffect(() => {
    // Get vehicle details on mount
    getVehicle(id);
  }, [getVehicle, id]);

  // Handle upload progress updates from Pictures section
  const handleUploadProgressChange = useCallback((progress: UploadProgress) => {
    setPicturesUploadProgress(progress);
  }, []);

  const submitData = useMemo(() => {
    return {
      ...generalSectionSubmitData,
      ...financialsSectionSubmitData,
      ...shippingSectionSubmitData,
      ...detailsSectionSubmitData,
      ...documentsSectionSubmitData,
    };
  }, [
    generalSectionSubmitData,
    financialsSectionSubmitData,
    shippingSectionSubmitData,
    detailsSectionSubmitData,
    documentsSectionSubmitData,
  ]);

  useEffect(() => {
    if (!submitData.stock || !submitData.name || !submitData.vin) {
      return setIsSaveEnabled(false);
    }
    if (addingNewVehicle) {
      // Adding new vehicle
      return setIsSaveEnabled(true);
    } else if (
      vehicle &&
      ((vehicle.account?.id || null) !== submitData?.account_id ||
        (vehicle.location?.id || null) !== submitData?.location_id ||
        vehicle.stock !== submitData?.stock ||
        vehicle.name !== submitData?.name ||
        vehicle.fuel_type !== submitData?.fuel_type ||
        vehicle.weight !== submitData?.weight ||
        vehicle.keys !== submitData?.keys ||
        vehicle.vendor !== submitData?.vendor ||
        vehicle.warehouse_delivery?.value !== submitData?.warehouse_delivery ||
        vehicle.invoice_date !== submitData?.invoice_date ||
        vehicle.sale_amount !== submitData?.sale_amount ||
        vehicle.fees_amount !== submitData?.fees_amount ||
        vehicle.gst_amount !== submitData?.gst_amount ||
        vehicle.pst_amount !== submitData?.pst_amount ||
        vehicle.total_amount !== submitData?.total_amount ||
        vehicle.vin !== submitData?.vin ||
        vehicle.technical_status?.value !== submitData?.technical_status ||
        vehicle.status?.value !== submitData?.status ||
        (vehicle.carrier?.id || null) !== submitData?.carrier_id ||
        (vehicle.client?.id || null) !== submitData?.client_id ||
        (vehicle.destination?.id || null) !== submitData?.destination_id ||
        (vehicle.importer?.id || null) !== submitData?.importer_id ||
        (vehicle.notify?.id || null) !== submitData?.notify_id ||
        vehicle.comment !== submitData?.comment ||
        vehicle.comment_restricted !== submitData?.comment_restricted ||
        vehicle.loader !== submitData?.loader ||
        vehicle.shipping_line?.value !== submitData?.shipping_line ||
        vehicle.container_number !== submitData?.container_number ||
        vehicle.title_status !== submitData?.title_status)
    ) {
      // Editing existing vehicle, compare if something changed
      return setIsSaveEnabled(true);
    }

    // Nothing changed
    return setIsSaveEnabled(false);
  }, [addingNewVehicle, submitData, vehicle]);

  const deleteVehicle = () => {
    if (vehicle) {
      if (isVehicleDeleted) {
        // Permanently delete the vehicle
        axiosService
          .delete(`/vehicles/vehicle/${vehicle.id}/`)
          .then(() => {
            navigate('/home/vehicles');
          })
          .catch((err: ErrorResponse) => setErrorMessage(err))
          .finally(() => setDeleteDialogOpen(false));
      } else {
        // Temporarily delete the vehicle (change its status to DELETED)
        axiosService
          .patch(`/vehicles/vehicle/${vehicle.id}/`, { status: STATUS_DELETED })
          .then(vehicleData => setVehicle(vehicleData.data))
          .catch((err: ErrorResponse) => setErrorMessage(err))
          .finally(() => setDeleteDialogOpen(false));
      }
    }
  };

  const getPOAFile = () => {
    const importers = shippingSectionRef.current?.getImporters() || [];
    return importers.find(importer => importer.id === importerChoice?.id)?.poa;
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (!addingNewVehicle && vehicle) {
      // Update existing vehicle
      axiosService
        .patch(`/vehicles/vehicle/${vehicle.id}/`, submitData)
        .then(vehicleData => {
          setErrorMessage(null);
          updateVehicleData(vehicleData.data);
        })
        .catch((err: ErrorResponse) => {
          // @ts-ignore:next-line
          const fieldError = err.response?.data?.vin || err.response?.data?.stock;
          if (fieldError) {
            // VIN or stock field error
            setErrorMessage({
              status: '',
              statusText: 'Error',
              message: fieldError[0],
            });
            return;
          }
          setErrorMessage(err);
        })
        .finally(() => setIsLoading(false));
    } else {
      // Create new vehicle
      axiosService
        .post('/vehicles/vehicle/', submitData)
        .then(vehicleData => {
          setErrorMessage(null);
          // Vehicle created, now upload pictures and files
          // Use Promise.allSettled to handle partial failures gracefully
          const uploadPromises = [];

          // Upload pictures using pictures section ref
          const pictureFiles = picturesSectionRef.current?.getTemporaryPictureFiles() || [];
          if (pictureFiles.length > 0) {
            uploadPromises.push(picturesSectionRef.current?.uploadNewVehiclePictures(vehicleData.data.id));
          }

          // Upload files using documents section ref
          const documentFiles = documentsSectionRef.current?.getTemporaryFiles() || [];
          if (documentFiles.length > 0) {
            uploadPromises.push(documentsSectionRef.current?.uploadNewVehicleFiles(vehicleData.data.id));
          }

          if (uploadPromises.length > 0) {
            setIsLoading(true);
            return Promise.allSettled(uploadPromises).then(results => {
              // Check for any failed uploads
              const failedUploads = results.filter(result => result.status === 'rejected');
              if (failedUploads.length > 0) {
                console.warn('Some file uploads failed during vehicle creation:', failedUploads);
                // Error messages will be shown by individual upload functions
              }
              // Navigate to vehicle page regardless of upload results
              navigate(`/home/vehicles/${vehicleData.data.id}`);
            });
          }
          // No files to upload, navigate immediately
          navigate(`/home/vehicles/${vehicleData.data.id}`);
        })
        .catch((err: ErrorResponse) => {
          // @ts-ignore:next-line
          const fieldError = err.response?.data?.vin || err.response?.data?.stock;
          if (fieldError) {
            // VIN or stock field error
            setErrorMessage({
              status: '',
              statusText: 'Error',
              message: fieldError[0],
            });
            return;
          }
          setErrorMessage(err);
        })
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <>
      <Box sx={vehiclePageStyles.container}>
        <Box sx={vehiclePageStyles.headerContainer}>
          <Box sx={{ display: 'flex', gap: { xs: '10px', sm: '20px' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            {vehicle ? (
              <>
                <Typography color="text" sx={vehiclePageStyles.headerTitle}>
                  {vehicle?.name.trim()}
                </Typography>
                <VehicleStatus
                  {...(vehicle?.status || {
                    value: newEntryStatus,
                    // eslint-disable-next-line camelcase
                    display_name: newEntryStatusLabel,
                  })}
                />
              </>
            ) : null}
          </Box>
          <Box sx={vehiclePageStyles.actionsContainer}>
            {shouldShowSaveButton() ? (
              <ActionButton
                type="save"
                disabled={!isSaveEnabled}
                onClick={e => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  handleSubmit();
                }}
              >
                Save
              </ActionButton>
            ) : null}
            {isSuperuser && vehicle ? (
              <ActionButton
                type="delete"
                onClick={e => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
              >
                {isVehicleDeleted ? 'Permanent Delete' : 'Delete'}
              </ActionButton>
            ) : null}
          </Box>
        </Box>
        <Box sx={vehiclePageStyles.contentBox}>
          <Box sx={vehiclePageStyles.column}>
            <VehiclePicturesSection
              ref={picturesSectionRef}
              vehicleId={vehicle?.id}
              initialPictures={vehicle?.pictures || []}
              initialTabs={vehicle?.tabs || []}
              onError={setErrorMessage}
              setIsLoading={setIsLoading}
              onUploadProgressChange={handleUploadProgressChange}
              canEdit={canEdit}
              onVehicleDataRefresh={() => getVehicle(vehicle?.id)}
            />
            {vehicle?.id && canViewSection(userType, SECTION_PUBLIC_LINK) ? (
              <VehiclePublicLinkSection vehicleId={vehicle.id} />
            ) : null}
          </Box>
          <Box sx={vehiclePageStyles.column}>
            <VehicleDetailsSection
              ref={detailsSectionRef}
              updateSubmitData={setDetailsSectionSubmitData}
              setIsLoading={setIsLoading}
              canView={canView}
              canEdit={canEdit}
            />
            <VehicleGeneralSection
              ref={generalSectionRef}
              updateSubmitData={setGeneralSectionSubmitData}
              canView={canView}
              canEdit={canEdit}
              isSuperuser={isSuperuser}
              newEntryStatus={newEntryStatus}
              newEntryStatusLabel={newEntryStatusLabel}
              user={user}
            />
            {canViewSection(userType, SECTION_FINANCIALS) ? (
              <VehicleFinancialsSection
                ref={financialsSectionRef}
                updateSubmitData={setFinancialsSectionSubmitData}
                canEdit={canEdit}
              />
            ) : null}
          </Box>
          <Box sx={vehiclePageStyles.column}>
            <VehicleShippingSection
              ref={shippingSectionRef}
              updateSubmitData={setShippingSectionSubmitData}
              canView={canView}
              canEdit={canEdit}
              isSuperuser={isSuperuser}
            />
            {canViewSection(userType, SECTION_DOCUMENTS) ? (
              <VehicleDocumentsSection
                ref={documentsSectionRef}
                vehicleId={vehicle?.id}
                initialFiles={vehicle?.files || []}
                onError={setErrorMessage}
                setIsLoading={setIsLoading}
                updateSubmitData={setDocumentsSectionSubmitData}
                importerChoice={importerChoice}
                onPOAUploaded={() => shippingSectionRef.current?.refreshImportersList()}
                getPOAFile={getPOAFile}
                canEdit={canEdit}
                isSuperuser={isSuperuser}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">
          {isVehicleDeleted ? 'Permanently delete vehicle' : 'Delete vehicle'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {isVehicleDeleted
              ? 'Are you sure you want to PERMANENTLY delete this vehicle?'
              : "Are you sure you want to change this vehicle's status to DELETED?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteVehicle} autoFocus>
            {isVehicleDeleted ? 'Permanent Delete' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <ModalErrorPopup
        title={errorMessage?.statusText}
        message={errorMessage?.message}
        onClose={() => setErrorMessage(null)}
      />
      {Boolean(isLoading || picturesUploadProgress.isUploading) && (
        <CircularProgressWithLabel
          value={picturesUploadProgress.isUploading ? picturesUploadProgress.percentage : undefined}
          label={
            picturesUploadProgress.currentFile && picturesUploadProgress.totalFiles
              ? picturesUploadProgress.label ??
                `Uploading ${picturesUploadProgress.currentFile} of ${picturesUploadProgress.totalFiles} files`
              : undefined
          }
        />
      )}
    </>
  );
}

/**
 * Wrapper that forces a full remount of VehiclePage when the route :id changes.
 * Without this, React reuses the same component instance and stale state persists.
 */
export default function VehiclePageRoute() {
  const { id } = useParams();
  return <VehiclePage key={id} />;
}
